// ---------------------------------------------------------------------------
// Hash router
// ---------------------------------------------------------------------------
//
// Owns window.location.hash. Query params belong in the URL search string
// (managed by url-state).
//
// Hash-query parameters (e.g. #/users?foo=bar) are NOT supported by design.
//
// pageKey is the full path without leading slash. Registry keys must match.
//   /users         → 'users'
//   /content/pages → 'content/pages'

/**
 * Normalize a raw path string to canonical form.
 * - Strips '#' prefix
 * - Strips anything after '?' (hash-query not supported)
 * - Ensures leading '/'
 * - Strips trailing '/' (but keeps '/' as '/')
 *
 * @param {string} raw
 * @returns {string} Canonical path (e.g. '/users')
 */
export function normalizePath(raw) {
  let path = raw;

  // Strip hash prefix
  if (path.startsWith('#')) {
    path = path.slice(1);
  }

  // Strip hash-query (not supported)
  const qIndex = path.indexOf('?');
  if (qIndex !== -1) {
    path = path.slice(0, qIndex);
  }

  // Ensure leading slash
  if (!path.startsWith('/')) {
    path = '/' + path;
  }

  // Strip trailing slash (but keep bare '/')
  if (path.length > 1 && path.endsWith('/')) {
    path = path.slice(0, -1);
  }

  return path;
}

/**
 * Create a hash-based router.
 *
 * @param {{ defaultRoute: string }} options
 * @returns {import('../../contracts/router.js').Router}
 */
export function createRouter({ defaultRoute }) {
  const normalizedDefault = normalizePath(defaultRoute);

  /** @type {Set<import('../../contracts/router.js').RouterListener>} */
  const listeners = new Set();

  /** @type {string | null} */
  let lastEmittedPath = null;

  /**
   * Get current canonical route from hash.
   * @returns {import('../../contracts/router.js').RouterRoute}
   */
  function currentRoute() {
    const hash = window.location.hash;
    const path = normalizePath(hash || '/');
    const pageKey = path === '/' ? '' : path.slice(1);
    return { path, pageKey };
  }

  /**
   * Navigate to a path. Normalizes input, writes canonical hash.
   * @param {string} path
   */
  function navigate(path) {
    const canonical = normalizePath(path);
    window.location.hash = '#' + canonical;
  }

  /**
   * Emit route to all listeners, with dedupe.
   * @param {import('../../contracts/router.js').RouterRoute} route
   */
  function emit(route) {
    if (route.path === lastEmittedPath) return;

    // Update lastEmittedPath BEFORE calling listeners (re-entrancy safe).
    // When hash is empty, subscribe → currentRoute() sees '/' → navigate(default)
    // → sets hash → hashchange fires. lastEmittedPath must already be set
    // so the hashchange handler dedupes correctly.
    lastEmittedPath = route.path;

    for (const listener of listeners) {
      listener(route);
    }
  }

  function onHashChange() {
    const route = currentRoute();

    // Empty/root hash → navigate to default route
    if (route.pageKey === '') {
      navigate(normalizedDefault);
      return;
    }

    emit(route);
  }

  window.addEventListener('hashchange', onHashChange);

  /**
   * Subscribe to route changes. Emits current route immediately.
   * @param {import('../../contracts/router.js').RouterListener} listener
   * @returns {() => void} Unsubscribe function
   */
  function subscribe(listener) {
    listeners.add(listener);

    // Emit current route immediately to THIS listener only (not all listeners).
    // Contract: subscribe() ALWAYS calls listener synchronously with current route.
    // Using listener(route) directly instead of emit() prevents re-notifying
    // previously subscribed listeners when a new subscriber is added.
    const route = currentRoute();
    if (route.pageKey === '') {
      // Navigate to default — sets hash and triggers hashchange.
      navigate(normalizedDefault);
      // Satisfy contract: call this listener with the now-current default route.
      // Update lastEmittedPath so the subsequent hashchange dedupes for all.
      const defaultRoute = currentRoute();
      lastEmittedPath = defaultRoute.path;
      listener(defaultRoute);
    } else {
      lastEmittedPath = route.path;
      listener(route);
    }

    return () => {
      listeners.delete(listener);
    };
  }

  function destroy() {
    window.removeEventListener('hashchange', onHashChange);
    listeners.clear();
    lastEmittedPath = null;
  }

  return { currentRoute, navigate, subscribe, destroy };
}
